<script setup>
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useUsers } from "../../Composables/useUsers.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import { useSubscriptionStore } from "../../Store/SubscriptionStore.js"
import _ from 'lodash'
import axios from "axios"
import DataTable from "../../Components/DataTable.vue"
import FormSelectField from "../../Components/FormSelectField.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const authUserStore = useAuthUserStore()
const subscriptionStore = useSubscriptionStore()
const authUser = computed(() => authUserStore.authUser)
const billingEnabled = computed(() => subscriptionStore.billingEnabled)

const storageKey = "Shiftech_UserList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

// FormSelectField attend un objet d'erreurs, inutilisé pour un filtre.
const errors = ref(null)
const filters = reactive({ subscription_status: null })
const paymentIssuesCount = ref(0)

const {
    url,
    data: users,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
    showArchived,
    getArchived,
    hideArchived,
} = useDataManager({
    baseUrl: '/api/users',
    baseSearchUrl: '/api/search/users',
    filters,
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        last_name: 'asc',
        role: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const subscriptionStatusOptions = [
    { text: 'Tous les abonnements', value: null },
    { text: 'Abonnés', value: 'subscribed' },
    { text: 'Non abonnés', value: 'not_subscribed' },
    { text: 'Impayés', value: 'payment_issue' },
    { text: 'Annulés', value: 'canceled' },
]

// La recherche texte passe par Meilisearch, qui n'indexe pas l'abonnement :
// le filtre ne s'applique donc qu'à la liste, pas aux résultats de recherche.
const subscriptionFilterDisabled = computed(() => searchLoaded.value)

function applySubscriptionFilter(value) {
    filters.subscription_status = value
    loadData()
}

// La recherche texte passe par Meilisearch, qui ignore le filtre : on quitte le
// mode recherche pour que le clic sur « impayés » produise bien un résultat.
function showPaymentIssues() {
    query.value = ''
    searchLoaded.value = false
    applySubscriptionFilter('payment_issue')
}

// Pas de garde sur billingEnabled : le store est alimenté de façon asynchrone
// et vaut encore false au premier affichage. L'endpoint renvoie 0 de lui-même
// quand la facturation est désactivée.
async function fetchPaymentIssuesCount() {
    try {
        const response = await axios.get('/api/users/subscription-alerts')
        paymentIssuesCount.value = response.data?.data?.payment_issues ?? 0
    } catch {
        paymentIssuesCount.value = 0
    }
}

const columns = computed(() => [
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Nom', field: 'last_name', sortable: true, slot: 'name', class: 'col-2' },
    { label: 'Rôle', field: 'role', sortable: true, slot: 'role', class: 'col-1' },
    { label: 'Email', field: 'email', class: 'col-2' },
    { label: 'Téléphone', field: 'phone_number', class: billingEnabled.value ? 'col-1' : 'col-2' },
    { label: 'Abonnement', slot: 'subscription', class: 'col-1', visible: billingEnabled.value },
    { label: 'Date création', field: 'created_at', sortable: true, slot: 'created_at', class: 'col-1' },
    { label: 'Dernière activité', field: 'last_activity', slot: 'last_activity', class: 'col-1' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        showArchived.value = savedSettings.showArchived || false
        filters.subscription_status = savedSettings.subscriptionStatus || null
    }

    loadData(url.value)
    fetchPaymentIssuesCount()
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded, showArchived, () => filters.subscription_status],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded, newShowArchived, newSubscriptionStatus]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            showArchived: newShowArchived,
            subscriptionStatus: newSubscriptionStatus,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToArchive = ref(null)

function showModal(modalId, id) {
    idToArchive.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    archiveUser,
    restoreUser,
} = useUsers({
    onArchived: () => {
        loadData(url.value)
        hideModal('modalArchive')
    },
    onRestored: () => {
        loadData(url.value)
    },
    onError: (error) => httpErrorsHandler(error),
})
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card" id="contactList">
                <div class="card-header">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchUsers">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>
                        <div class="col-md-3" v-if="billingEnabled">
                            <div :title="subscriptionFilterDisabled ? 'Filtre indisponible pendant une recherche' : ''">
                                <form-select-field
                                    name="subscription_status"
                                    label="Abonnement"
                                    :label-hidden="true"
                                    :errors="errors"
                                    :data="filters.subscription_status"
                                    :disabled="subscriptionFilterDisabled"
                                    @update:field="applySubscriptionFilter($event)"
                                    placeholder="Tous les abonnements"
                                    additional-class="bg-light border-light"
                                    bs-class="col-12"
                                    margin="mb-0"
                                    :liste-options="subscriptionStatusOptions"
                                />
                            </div>
                        </div>
                        <div class="col-md-auto ms-auto">
                            <button
                                type="button"
                                class="btn btn-danger waves-light me-2"
                                v-if="billingEnabled && paymentIssuesCount > 0"
                                @click="showPaymentIssues"
                            >
                                <i class="ri-error-warning-fill me-1 align-bottom"></i>
                                {{ paymentIssuesCount }} impayé{{ paymentIssuesCount > 1 ? 's' : '' }}
                            </button>
                            <button type="button" class="btn btn-dark waves-light me-2" @click="getArchived" v-if="!showArchived">
                                <i class="ri-archive-fill me-1 align-bottom"></i> Archives
                            </button>
                            <button type="button" class="btn btn-success waves-light me-2" @click="hideArchived" v-else>
                                <i class="ri-contacts-fill me-1 align-bottom"></i> Utilisateurs
                            </button>
                            <router-link :to="{ name:'users.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_user">
                                <i class="ri-add-fill align-bottom me-1"></i> Ajouter
                            </router-link>
                        </div>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="users"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Nom avec avatar -->
                        <template #name="{ item }">
                            <div class="d-flex align-items-center">
                                <div class="flex-shrink-0">
                                    <img :src="'/format-image/'+item.data.profile_picture+'?w=32&h=32&fm=webp&fit=crop'" alt="" class="avatar-xs rounded-circle">
                                </div>
                                <div class="flex-grow-1 ms-2 name">{{ item.data.last_name }} {{ item.data.first_name }}</div>
                            </div>
                        </template>

                        <!-- Rôle -->
                        <template #role="{ item }">
                            <span :class="'badge fs-11 bg-'+item.data.role_badge_color+'-subtle text-'+item.data.role_badge_color">
                                {{ item.data.role_label_fr }}
                            </span>
                        </template>

                        <!-- Date création -->
                        <template #created_at="{ item }">
                            {{ item.data.created_at_fr }}
                        </template>

                        <!-- Dernière activité -->
                        <template #last_activity="{ item }">
                            {{ item.data.last_activity_fr }}
                        </template>

                        <!-- Abonnement -->
                        <template #subscription="{ item }" v-if="billingEnabled">
                            <span
                                v-if="item.data.subscription_status"
                                :class="'badge fs-11 bg-'+item.data.subscription_status_badge_color+'-subtle text-'+item.data.subscription_status_badge_color"
                            >
                                {{ item.data.subscription_status_label_fr }}
                            </span>
                            <span v-else class="text-muted">-</span>
                            <div class="text-muted fs-11 mt-1" v-if="item.data.subscription_plan">
                                {{ item.data.subscription_plan }}
                            </div>
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'users.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">
                                Accéder
                            </router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'users.edit', params: { id: item.data.id } }" class="text-info" title="Modifier l'utilisateur" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- ARCHIVE -->
                            <template v-if="!showArchived">
                                <a href="#" class="text-danger" title="Archiver l'utilisateur" @click.prevent="showModal('modalArchive', item.data.id)" v-if="item.can.delete">
                                    <i class="ri-inbox-archive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-archive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>

                            <!-- RESTORE -->
                            <template v-else>
                                <a href="#" class="text-success" title="Restaurer l'utilisateur" @click.prevent="restoreUser(item.data.id)" v-if="item.can.restore">
                                    <i class="ri-inbox-unarchive-fill fs-17 px-1"></i>
                                </a>
                                <i class="ri-inbox-unarchive-fill fs-17 px-1 text-muted" v-else></i>
                            </template>
                        </template>
                    </data-table>

                    <modal id="modalArchive" title="Archiver l'utilisateur ?" btn-class="btn-danger" btn-text="Archiver" @click:button="archiveUser(idToArchive)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point d'archiver un utilisateur. Continuer ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
